/**
 * Geometry helpers for the public live trip page: trim the road route so it
 * starts at the bus (the line shrinks as the bus drives, like the app), and
 * tell when the bus has left the route so it can be fetched again.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_M = 6_371_000;

/** Great-circle distance in metres. */
export function distanceM(a: LatLng, b: LatLng): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Closest point to `p` on segment a→b (flat projection, fine at street scale). */
function projectOnSegment(p: LatLng, a: LatLng, b: LatLng): { point: LatLng; t: number } {
  const kx = Math.cos((p.lat * Math.PI) / 180);
  const ax = a.lng * kx;
  const bx = b.lng * kx;
  const px = p.lng * kx;
  const dx = bx - ax;
  const dy = b.lat - a.lat;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (p.lat - a.lat) * dy) / len2));
  return { point: { lat: a.lat + t * dy, lng: a.lng + t * (b.lng - a.lng) }, t };
}

/**
 * The part of `path` still ahead of `pos`, starting exactly at the bus, and
 * how far the bus is from the route in metres.
 */
export function remainingPath(path: LatLng[], pos: LatLng): { path: LatLng[]; offRouteM: number } {
  if (path.length < 2) return { path: [pos, ...path], offRouteM: path.length ? distanceM(pos, path[0]) : 0 };
  let bestIx = 0;
  let bestM = Infinity;
  for (let i = 0; i < path.length - 1; i++) {
    const { point } = projectOnSegment(pos, path[i], path[i + 1]);
    const m = distanceM(pos, point);
    if (m < bestM) {
      bestM = m;
      bestIx = i;
    }
  }
  return { path: [pos, ...path.slice(bestIx + 1)], offRouteM: bestM };
}

/** Linear interpolation between two positions. */
export function lerpLatLng(a: LatLng, b: LatLng, t: number): LatLng {
  return { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t };
}

/**
 * Point `fraction` (0..1) of the way along `path` by distance. Used to glide
 * the bus along the road between two fixes instead of cutting corners.
 */
export function pointAlong(path: LatLng[], fraction: number): LatLng {
  if (path.length === 0) throw new Error('empty path');
  if (path.length === 1 || fraction <= 0) return path[0];
  const legs = path.slice(1).map((p, i) => distanceM(path[i], p));
  const total = legs.reduce((s, m) => s + m, 0);
  if (total === 0 || fraction >= 1) return path[path.length - 1];
  let target = total * fraction;
  for (let i = 0; i < legs.length; i++) {
    if (target <= legs[i]) return lerpLatLng(path[i], path[i + 1], legs[i] === 0 ? 0 : target / legs[i]);
    target -= legs[i];
  }
  return path[path.length - 1];
}

/**
 * The road between two fixes, taken from the route when both sit on it, so
 * the bus follows bends instead of cutting across them. Falls back to the
 * straight line.
 */
export function roadBetween(route: LatLng[], from: LatLng, to: LatLng, maxOffM = 60): LatLng[] {
  if (route.length < 2) return [from, to];
  const nearestSeg = (p: LatLng) => {
    let ix = 0;
    let best = Infinity;
    for (let i = 0; i < route.length - 1; i++) {
      const m = distanceM(p, projectOnSegment(p, route[i], route[i + 1]).point);
      if (m < best) {
        best = m;
        ix = i;
      }
    }
    return { ix, m: best };
  };
  const a = nearestSeg(from);
  const b = nearestSeg(to);
  if (a.m > maxOffM || b.m > maxOffM || b.ix < a.ix) return [from, to];
  return [from, ...route.slice(a.ix + 1, b.ix + 1), to];
}
